import React, { useMemo } from 'react';
import { PubNubProvider } from 'pubnub-react';
import { useGetUserDetailsQuery } from 'api/queries/customer/get-one';
import { useDataValues } from 'providers/DataValues';
import { useChatModal } from 'providers/ChatModalProvider';
import SectionMessagesList from 'components/chat/components/section.messages-list';

type Props = {
  /** Aimeos trade/product ID from the dispute's escrow relation. Optional because the relation may not be loaded. */
  tradeProductId?: number;
};

/**
 * Renders the inline trade-chat panel on the Dispute Details page.
 *
 * Looks up the PubNub channel whose `custom.trade_id` matches `tradeProductId`,
 * then renders `SectionMessagesList` inside a `PubNubProvider`. Handles loading,
 * missing-channel, and missing-trade states gracefully.
 *
 * Side-effects: calls `useGetUserDetailsQuery` to resolve the counterpart user's
 * username, which issues a network request when a matching channel is found.
 */
export default function DisputeChatPanel({ tradeProductId }: Props) {
  const {
    chatClient,
    loadingMemberships,
    state: { memberships },
  } = useChatModal();

  const { userId } = useDataValues();

  // find the channel whose trade_id matches the dispute's trade product ID
  const activeChannel = useMemo(
    () => (tradeProductId != null ? memberships.find((m) => m.custom?.trade_id == tradeProductId) : undefined),
    [memberships, tradeProductId],
  );

  // derive the counterpart user ID using the same logic as ChatContent
  const anotherUserId = (
    activeChannel?.custom?.trade_creator_id == userId
      ? activeChannel?.custom?.order_creator_id
      : activeChannel?.custom?.trade_creator_id
  )?.toString();

  const { data: anotherUser, isLoading: loadingAnotherUser } = useGetUserDetailsQuery(
    anotherUserId ?? '',
    true,
    !!anotherUserId,
  );

  // build the full channel ID list so PubNub Chat subscribes to all channels
  const channelsIDs = useMemo(() => memberships.map((m) => m.id), [memberships]);

  // trade product ID is absent — escrow relation was not loaded
  if (tradeProductId == null) {
    return (
      <div className="h-full min-h-[400px] lg:min-h-0 flex flex-col overflow-hidden border-l border-l-neutral-100">
        <p className="text-sm text-gray-500 px-4 py-8 text-center">Chat unavailable</p>
      </div>
    );
  }

  // chat client not yet initialised or memberships still paginating
  if (!chatClient || loadingMemberships) {
    return (
      <div className="h-full min-h-[400px] lg:min-h-0 flex flex-col overflow-hidden border-l border-l-neutral-100">
        <p className="text-sm text-gray-400 px-4 py-8 text-center">Loading chat…</p>
      </div>
    );
  }

  // memberships loaded but no channel matched the trade product ID
  if (!activeChannel) {
    return (
      <div className="h-full min-h-[400px] lg:min-h-0 flex flex-col overflow-hidden border-l border-l-neutral-100">
        <p className="text-sm text-gray-500 px-4 py-8 text-center">No chat found for this trade</p>
      </div>
    );
  }

  return (
    <PubNubProvider client={chatClient}>
      {/* hide the mobile back-arrow button — it has no target channel list in this inline context */}
      <div className="h-full min-h-[400px] lg:min-h-0 flex flex-col overflow-hidden [&>section>header>button:first-child]:hidden">
        <SectionMessagesList
          activeChannel={activeChannel}
          anotherUsername={anotherUser?.username}
          channels={channelsIDs}
          loadingAnotherUser={loadingAnotherUser}
          setActiveChannel={() => {}}
        />
      </div>
    </PubNubProvider>
  );
}
